import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, useTheme} from "@emotion/react";
import {Dispatch} from "redux";

import {CenterBox} from "@pg-design/grid-module";
import {borderRadius, h100, pointer, w100} from "@pg-design/helpers-css";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {useUserDevice} from "@pg-mono/user-device";

import {ColumnListItem} from "../atoms/ColumnListItem";
import {SmartAdSASTypes} from "../offer/helpers/smart_add_sas_types";
import {gtmHeroBanerClick} from "../tracking/google_tag_manager/gtm_hero_banner";
import {changeSsrTdaShow, ISmartAdActions} from "./actions/add_smart_offer_action";
import {TdaDesktopWrap} from "./components/TdaDesktopWrap";
import {TdaMobileWrap} from "./components/TdaMobileWrap";
import {useTdaPlaceholderBanner} from "./hooks/use_tda_placeholder_banner";
import {selectSmartAdOffer} from "./reducer/smart_ads_reducer";
import {tdaMobileImageHolder} from "./styles/tda_mobile_image_holder";
import {encodeTargetUrl} from "./utils/encode_target_url";
import {getInternalBanner} from "./utils/get_internal_banner";
import {Creation} from "./Creation";

const MAX_SMART_AD_SERVER_PLACEMENTS = 15;

interface IProps {
    adFormat: number;
    className?: string;
    suffix?: number;
    referenceElementId?: string;
    preventGtmOnMobile?: boolean;
    fetchPriority?: "high" | "low" | "auto";
}

export const SmartAdServerPlacement = (props: IProps) => {
    const {adFormat, className, suffix = 1, fetchPriority} = props;
    const formatId = !suffix || suffix < 2 ? adFormat.toString() : `${adFormat}_${suffix}`;
    const placementId = `sas_${formatId}`;

    const {isMobile} = useUserDevice();
    const theme = useTheme();
    const dispatch: Dispatch<ISmartAdActions> = useDispatch();

    const currentSmartAdOffer = useSelector(selectSmartAdOffer(placementId));
    const adPlaceholderRef = useRef<HTMLDivElement | null>(null);
    const creationRenderedRef = useRef(false);
    const [externalHtmlCleared, setExternalHtmlCleared] = useState(false);
    /**
     * TODO: HERO is now replaced with TDA creation, in future, remove all related code if it stays that way,
     *  _canRenderInternalBanner is used only in hero logic
     */
    const [canRenderInternalBanner, setCanRenderInternalBanner] = useState<boolean>(false);
    const [sasPlacementHasAd, setSasPlacementHasAd] = useState<boolean | null>(null);
    const [initComplete, setInitComplete] = useState<boolean>(false);
    const tdaInternalBanner = useTdaPlaceholderBanner();

    const internalBanner = useMemo(() => getInternalBanner(adFormat), [canRenderInternalBanner]);

    useEffect(() => {
        let initTimeout = 0;
        let initAttempts = 0;

        const initPlacement = () => {
            initTimeout = window.setTimeout(() => {
                const win = window as any; // eslint-disable-line @typescript-eslint/no-explicit-any

                const noSas = win.sas == null || win.sas.cmd == null;

                initAttempts = ++initAttempts;

                if (noSas && initAttempts > 20 && !currentSmartAdOffer) {
                    setCanRenderInternalBanner(true);
                }

                if (noSas && initAttempts > 20) {
                    return console.warn("Cannot load ads script");
                }

                if (noSas) {
                    initPlacement();
                    return;
                }

                setInitComplete(true);
                win.sas.cmd.push(function () {
                    win.sas.render(formatId);
                });
            }, 200);
        };

        initPlacement();

        return () => {
            clearTimeout(initTimeout);
        };
    }, []);

    useEffect(() => {
        const intervalTime = 200;
        const intervalMaxTime = 3000;

        let sasAdInterval = 0;
        let currentIntervalTime = 0;

        const validateAd = () => {
            sasAdInterval = window.setInterval(() => {
                currentIntervalTime += intervalTime;
                const isIntervalMaxTime = currentIntervalTime >= intervalMaxTime;

                if (isIntervalMaxTime && !currentSmartAdOffer) {
                    setSasPlacementHasAd(false);
                }

                if (isIntervalMaxTime) {
                    clearInterval(sasAdInterval);
                }

                if (currentSmartAdOffer) {
                    setSasPlacementHasAd(true);
                    clearInterval(sasAdInterval);
                }
            }, 200);
        };

        validateAd();

        return () => {
            clearInterval(sasAdInterval);
        };
    }, [initComplete, currentSmartAdOffer]);

    useEffect(() => {
        /**
         * External script (SmartAdServer) adds its own html (scripts, tags) to and element with id = placementId
         * We remove this and render our own component instead
         */
        if (adPlaceholderRef.current && currentSmartAdOffer) {
            adPlaceholderRef.current.innerHTML = "";
            setCanRenderInternalBanner(false);
            setExternalHtmlCleared(true);
        }

        if (adFormat === SmartAdSASTypes.HERO && !currentSmartAdOffer && initComplete) {
            setCanRenderInternalBanner(true);
        }

        if (adFormat === SmartAdSASTypes.TDA && !currentSmartAdOffer && initComplete) {
            setCanRenderInternalBanner(true);
        }
    }, [currentSmartAdOffer, initComplete]);

    if (suffix && suffix > MAX_SMART_AD_SERVER_PLACEMENTS) {
        return null;
    }

    useEffect(() => {
        if (currentSmartAdOffer && !currentSmartAdOffer.didLoadCreationDetails) {
            setSasPlacementHasAd(true);
        }
    }, [currentSmartAdOffer?.didLoadCreationDetails]);

    useEffect(() => {
        if (adFormat === SmartAdSASTypes.TDA && typeof sasPlacementHasAd === "boolean") {
            dispatch<ISmartAdActions>(changeSsrTdaShow(false));
        }
    }, [sasPlacementHasAd]);

    useEffect(() => {
        if (adPlaceholderRef.current) {
            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    // init_agency_script may load ads again while we still have components in tree
                    const externalAdInjectedAfterRender = mutation.type === "childList" && creationRenderedRef.current;
                    if (externalAdInjectedAfterRender && adPlaceholderRef.current) {
                        adPlaceholderRef.current.innerHTML = "";
                    }
                }
            });

            observer.observe(adPlaceholderRef.current, {childList: true});
        }
    }, [adPlaceholderRef.current]);

    const placementHasAd = Boolean(externalHtmlCleared && currentSmartAdOffer);

    const onInternalBannerClick = () => {
        gtmHeroBanerClick();

        window.open(internalBanner?.link, "_blank");
    };

    const onTdaBannerClick = () => {
        gtmHeroBanerClick();

        window.open(tdaInternalBanner?.link, "_blank");
    };

    const renderCreation = () => {
        return (
            <>
                <div ref={adPlaceholderRef} id={placementId} css={adPlaceholder} />
                {(adFormat === SmartAdSASTypes.HERO || (adFormat === SmartAdSASTypes.TDA && isMobile)) &&
                    !externalHtmlCleared &&
                    sasPlacementHasAd === null && (
                        <CenterBox>
                            <Loader size="lg" />
                        </CenterBox>
                    )}
                {externalHtmlCleared && currentSmartAdOffer && currentSmartAdOffer.tagId && currentSmartAdOffer.creationDetails && (
                    <Creation
                        creation={currentSmartAdOffer.creationDetails}
                        adMeta={currentSmartAdOffer}
                        url={encodeTargetUrl(currentSmartAdOffer.clickUrl)}
                        metadata={{
                            sas_id: currentSmartAdOffer.tagId
                        }}
                        onAfterFirstRender={() => {
                            creationRenderedRef.current = true;
                        }}
                        referenceElementId={props.referenceElementId}
                        preventGtmOnMobile={props.preventGtmOnMobile}
                    />
                )}
                {adFormat === SmartAdSASTypes.HERO && sasPlacementHasAd === false && (
                    <Image
                        onClick={onInternalBannerClick}
                        css={heroBannerImage}
                        src={isMobile ? internalBanner?.mobileImg : internalBanner?.img}
                        alt="hero banner"
                        width={isMobile ? "375" : "1536"}
                        height={isMobile ? "484" : "295"}
                        fetchPriority={fetchPriority}
                    />
                )}
                {adFormat === SmartAdSASTypes.TDA && sasPlacementHasAd === false && isMobile && (
                    <TdaMobileWrap>
                        <Image
                            onClick={onTdaBannerClick}
                            css={tdaMobileImageHolder}
                            src={tdaInternalBanner?.mobileImg}
                            alt="tda banner"
                            width={isMobile ? "375" : "1100"}
                            height={isMobile ? "484" : "220"}
                            fetchPriority={fetchPriority}
                        />
                    </TdaMobileWrap>
                )}
                {adFormat === SmartAdSASTypes.TDA && sasPlacementHasAd === false && !isMobile && (
                    <TdaDesktopWrap backgroundColor={theme.colors.gray[100]}>
                        <Image
                            onClick={onTdaBannerClick}
                            css={tdaBannerImage}
                            src={tdaInternalBanner?.imgSet[0]}
                            src2x={tdaInternalBanner?.imgSet[1]}
                            src3x={tdaInternalBanner?.imgSet[2]}
                            alt="tda banner"
                            width={isMobile ? "375" : "1100"}
                            height={isMobile ? "484" : "220"}
                            fetchPriority={fetchPriority}
                        />
                    </TdaDesktopWrap>
                )}
            </>
        );
    };

    switch (adFormat) {
        case SmartAdSASTypes.HERO: {
            return isMobile ? (
                <ColumnListItem className={className} css={internalMobileBannerContainer}>
                    {renderCreation()}
                </ColumnListItem>
            ) : (
                <div css={internalBannerContainer}>{renderCreation()}</div>
            );
        }
        case SmartAdSASTypes.TDA: {
            return isMobile ? (
                <ColumnListItem className={className} css={internalMobileBannerContainer}>
                    {renderCreation()}
                </ColumnListItem>
            ) : (
                <>{renderCreation()}</>
            );
        }
        default: {
            return (
                <ColumnListItem className={className} css={adPlacementWrap(placementHasAd)}>
                    {renderCreation()}
                </ColumnListItem>
            );
        }
    }
};

// Styles
const adPlaceholder = css`
    display: none;
`;

const adPlacementWrap = (hasAd: boolean) => () => css`
    display: ${hasAd ? "list-item" : "none"};
`;

const internalBannerContainer = css`
    ${w100};
    ${pointer};
    height: 295px;
`;

const heroBannerImage = css`
    width: 375px;
    height: 456px;

    img {
        object-fit: cover;
        ${w100};
        ${h100};
        ${borderRadius(2)};
    }

    @media (min-width: 1025px) {
        width: 100%;
        height: 295px;
    }
`;

const internalMobileBannerContainer = css`
    width: 336px;

    @media (min-width: 400px) {
        width: 100%;
        height: 100%;
        max-width: 100%;
    }
`;

const tdaBannerImage = css`
    width: 1100px;
    height: 220px;
    cursor: pointer;
`;
